import type {
  BudgetModelConfig,
  CategoryId,
  ContractDocument,
  TimeLogRateOption,
} from '@timelog/pm-shared';
import {
  getCategory,
  isCategoryAvailable,
  seedAmountFirst,
  seedHoursFirst,
  setOwnHours,
} from '@timelog/pm-shared';

import { seedRateCardFromDefaults } from '../editor/rateCardSeed';

/**
 * The guided-entry state machine (CALC-18 / BR-21) — pure and client-only.
 * Each committed step derives a whole document from the captured BASELINE plus
 * the inputs so far (idempotent and order-safe: re-entering a step replaces,
 * never stacks); the caller dispatches that document to the contract store,
 * so autosave coalesces the walk into ordinary PUTs. Nothing flow-specific is
 * ever persisted.
 *
 * fixed-amount: amount → exp → sub → mat → ownHours → done   (seedAmountFirst)
 * tm:           the same walk, the same seed                    (seedAmountFirst)
 * fixed-hours:  hours → done                                  (seedHoursFirst)
 *
 * T&M walks exactly like fixed price (the pilot's ask, 2026-10-08): the total
 * amount first, then the visible sections, then Own Work hours. Whatever the
 * budget-model config says about the sections — which are present, which can
 * be added, what share Contingency is seeded at — holds for both billing
 * methods alike (ADR 0048). The one difference: committing a T&M amount gives
 * an EMPTY rate card its copy of the caller's default hourly rates
 * (`seedRateCardFromDefaults`), the moment the contract becomes a priced T&M
 * contract rather than a type label.
 */

export type FlowMode = 'fixed-amount' | 'fixed-hours' | 'tm';

export type FlowStep = 'amount' | 'exp' | 'sub' | 'mat' | 'ownHours' | 'hours';

export type FlowInputs = {
  amount?: number;
  exp?: number;
  sub?: number;
  mat?: number;
  hours?: number;
};

export type FlowState = {
  mode: FlowMode;
  step: FlowStep;
  baseline: ContractDocument;
  inputs: FlowInputs;
  /**
   * Categories the walk never visits: the ones the budget-model config makes
   * unavailable (BR-27), and the ones hidden on the contract when the walk
   * starts. Enter and Tab move through the sections the user can SEE — a hidden
   * one is added from the "add category" tile, never conjured by the walk.
   */
  skipCategories: CategoryId[];
};

export type FlowEvent =
  | { type: 'COMMIT'; value: number }
  /** Tab on an optional step: enter 0 and move on. */
  | { type: 'SKIP' }
  /** Esc / outside click: end the walk, keep everything already committed. */
  | { type: 'EXIT' };

export type FlowContext = {
  blendedCostRate: number;
  /** The caller's default hourly rates — what an empty T&M card is seeded from. */
  defaultRates?: readonly TimeLogRateOption[];
  /** BR-14 via the budget-model config; control 0.1, 0 seeds Contingency empty. */
  contingencyShare: number;
};

export type FlowResult = {
  /** Null = the flow ended (done or exited). */
  state: FlowState | null;
  /** When present, the caller applies this to the contract store. */
  document?: ContractDocument;
};

/**
 * The sections the walk passes over: unavailable ones (BR-27) and the ones
 * hidden on the contract right now. Enter/Tab only ever lands in a card the
 * user can already see — a hidden Expenses card must not appear because the
 * amount was confirmed.
 */
export const skippedCategories = (
  config: BudgetModelConfig,
  document: ContractDocument,
): CategoryId[] =>
  (['cont', 'exp', 'sub', 'mat'] as const).filter(
    (id) => !isCategoryAvailable(config, id) || getCategory(document, id).hidden,
  );

/**
 * The walk re-reads visibility from the LIVE contract before every step: a
 * section hidden (or re-added) while the walk is open is skipped (or visited)
 * from then on. Until the first value is committed the live contract also
 * becomes the baseline, or the first commit — derived from the stale baseline —
 * would put a card the user just hid straight back.
 */
export const withLiveVisibility = (
  state: FlowState,
  live: ContractDocument,
  config: BudgetModelConfig,
): FlowState => ({
  ...state,
  baseline: Object.keys(state.inputs).length === 0 ? live : state.baseline,
  skipCategories: skippedCategories(config, live),
});

export const startFlow = (
  mode: FlowMode,
  baseline: ContractDocument,
  skipCategories: CategoryId[] = [],
): FlowState => ({
  mode,
  step: mode === 'fixed-hours' ? 'hours' : 'amount',
  baseline,
  inputs: {},
  skipCategories,
});

const AMOUNT_WALK: FlowStep[] = ['amount', 'exp', 'sub', 'mat', 'ownHours'];

const nextStep = (state: FlowState): FlowStep | null => {
  const full = state.mode === 'fixed-hours' ? ['hours' as const] : AMOUNT_WALK;
  // 'own' is never skippable; the required first steps have no category.
  const walk = full.filter((step) => {
    const category = stepCategory(step);
    return category === null || category === 'own' || !state.skipCategories.includes(category);
  });
  const index = walk.indexOf(state.step);
  return walk[index + 1] ?? null;
};

/** The document the inputs-so-far describe — derived fresh from the baseline. */
const buildDocument = (state: FlowState, context: FlowContext): ContractDocument => {
  const { baseline, inputs } = state;

  if (state.mode === 'fixed-hours') {
    return inputs.hours && inputs.hours > 0
      ? seedHoursFirst(
          baseline,
          { hours: inputs.hours, blendedCostRate: context.blendedCostRate },
          { contingencyShare: context.contingencyShare },
        )
      : baseline;
  }

  // fixed-amount and tm
  if (!inputs.amount || inputs.amount <= 0) return baseline;
  let document = seedAmountFirst(
    baseline,
    {
      amount: inputs.amount,
      ...(inputs.exp === undefined ? {} : { exp: inputs.exp }),
      ...(inputs.sub === undefined ? {} : { sub: inputs.sub }),
      ...(inputs.mat === undefined ? {} : { mat: inputs.mat }),
    },
    { contingencyShare: context.contingencyShare },
  );
  if (state.mode === 'tm') {
    document = seedRateCardFromDefaults(document, context.defaultRates ?? []);
  }
  if (inputs.hours !== undefined && inputs.hours > 0) {
    document = setOwnHours(document, inputs.hours);
  }
  return document;
};

const inputKey = (step: FlowStep): keyof FlowInputs => {
  switch (step) {
    case 'amount':
      return 'amount';
    case 'exp':
    case 'sub':
    case 'mat':
      return step;
    case 'ownHours':
    case 'hours':
      return 'hours';
  }
};

/** The first step (amount / hours) needs a positive value; the rest accept 0. */
const isRequiredStep = (step: FlowStep): boolean => step === 'amount' || step === 'hours';

export const flowReducer = (
  state: FlowState,
  event: FlowEvent,
  context: FlowContext,
): FlowResult => {
  if (event.type === 'EXIT') {
    return { state: null };
  }

  const value = event.type === 'SKIP' ? 0 : Math.max(0, event.value);
  if (isRequiredStep(state.step) && value <= 0) {
    // Nothing to seed from yet — stay on the step (the input keeps focus).
    return { state };
  }

  const advanced: FlowState = {
    ...state,
    inputs: { ...state.inputs, [inputKey(state.step)]: value },
  };
  const document = buildDocument(advanced, context);
  const step = nextStep(advanced);

  return step === null ? { state: null, document } : { state: { ...advanced, step }, document };
};

/** Which category card a step edits inside (for the highlight ring + focus). */
export const stepCategory = (step: FlowStep): CategoryId | null => {
  switch (step) {
    case 'exp':
      return 'exp';
    case 'sub':
      return 'sub';
    case 'mat':
      return 'mat';
    case 'ownHours':
      return 'own';
    default:
      return null;
  }
};
